import { Stack, TextField } from '@mui/material';
import { useTranslate } from 'react-admin';
import type { Gateway, PaymentIntentStatus, PaymentIntentType, ReportOperationsFilters } from '@/api/models';
import { DateRangeFields } from './DateRangeFields';
import { EnumMultiSelect } from './EnumMultiSelect';
import { TagsField } from './TagsField';

const GATEWAY_VALUES: Gateway[] = ['redsys', 'stripe', 'adyen', 'payten', 'plexo'];
const STATUS_VALUES: PaymentIntentStatus[] = [
  'created',
  'session_consumed',
  'redirected',
  'authorized',
  'captured',
  'failed',
  'cancelled',
  'expired',
  'refunded',
  'partially_refunded',
];
const TYPE_VALUES: PaymentIntentType[] = ['sale', 'preauth', 'verify', 'terminal'];

const TENANT_TAG_PATTERN = /^[a-z0-9_-]+:.+$/;

interface OperationsFiltersFormProps {
  filters: ReportOperationsFilters;
  onChange: (patch: Partial<ReportOperationsFilters>) => void;
}

/**
 * Every query parameter `GET /v1/admin/reports/operations` and
 * `GET /v1/admin/reports/operations/export` accept, and no more: `dateFrom`,
 * `dateTo`, `tenantCode`, `tenantTag`, `applicationCode`, `gateway`,
 * `status`, `type`, `amountMin`, `amountMax`, `orderReference`. Pagination
 * and sorting live on the list view itself, not in this popover.
 */
export function OperationsFiltersForm({ filters, onChange }: OperationsFiltersFormProps) {
  const translate = useTranslate();

  return (
    <Stack spacing={2.5}>
      <DateRangeFields
        fromLabel={translate('pay.operations.filters.dateFrom')}
        toLabel={translate('pay.operations.filters.dateTo')}
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        onChangeFrom={(dateFrom) => onChange({ dateFrom })}
        onChangeTo={(dateTo) => onChange({ dateTo })}
      />
      <TagsField
        label={translate('pay.operations.filters.tenantCode')}
        helperText={translate('pay.operations.filters.tenantCodeHelp')}
        values={filters.tenantCode ?? []}
        onChange={(tenantCode) => onChange({ tenantCode: tenantCode.length > 0 ? tenantCode : undefined })}
      />
      <TagsField
        label={translate('pay.operations.filters.tenantTag')}
        helperText={translate('pay.operations.filters.tenantTagHelp')}
        values={filters.tenantTag ?? []}
        validate={(value) => TENANT_TAG_PATTERN.test(value)}
        onChange={(tenantTag) => onChange({ tenantTag: tenantTag.length > 0 ? tenantTag : undefined })}
      />
      <TagsField
        label={translate('pay.operations.filters.applicationCode')}
        values={filters.applicationCode ?? []}
        onChange={(applicationCode) =>
          onChange({ applicationCode: applicationCode.length > 0 ? applicationCode : undefined })
        }
      />
      <EnumMultiSelect
        label={translate('pay.operations.filters.gateway')}
        options={GATEWAY_VALUES.map((value) => ({ value, label: value }))}
        values={filters.gateway ?? []}
        onChange={(values) => onChange({ gateway: values.length > 0 ? (values as Gateway[]) : undefined })}
      />
      <EnumMultiSelect
        label={translate('pay.operations.filters.status')}
        options={STATUS_VALUES.map((value) => ({ value, label: translate(`pay.operations.status.${value}`) }))}
        values={filters.status ?? []}
        onChange={(values) => onChange({ status: values.length > 0 ? (values as PaymentIntentStatus[]) : undefined })}
      />
      <EnumMultiSelect
        label={translate('pay.operations.filters.type')}
        options={TYPE_VALUES.map((value) => ({ value, label: translate(`pay.operations.type.${value}`) }))}
        values={filters.type ?? []}
        onChange={(values) => onChange({ type: values.length > 0 ? (values as PaymentIntentType[]) : undefined })}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          type="number"
          size="small"
          label={translate('pay.operations.filters.amountMin')}
          helperText={translate('pay.operations.filters.amountHelp')}
          value={filters.amountMin ?? ''}
          inputProps={{ min: 0, step: 1 }}
          onChange={(event) => {
            const raw = event.target.value;
            onChange({ amountMin: raw === '' ? undefined : Math.max(0, Math.trunc(Number(raw))) });
          }}
          fullWidth
        />
        <TextField
          type="number"
          size="small"
          label={translate('pay.operations.filters.amountMax')}
          helperText={translate('pay.operations.filters.amountHelp')}
          value={filters.amountMax ?? ''}
          inputProps={{ min: 0, step: 1 }}
          onChange={(event) => {
            const raw = event.target.value;
            onChange({ amountMax: raw === '' ? undefined : Math.max(0, Math.trunc(Number(raw))) });
          }}
          fullWidth
        />
      </Stack>
      <TextField
        size="small"
        label={translate('pay.operations.filters.orderReference')}
        value={filters.orderReference ?? ''}
        onChange={(event) => onChange({ orderReference: event.target.value || undefined })}
        fullWidth
      />
    </Stack>
  );
}
